import { registerPlugin, WebPlugin } from '@capacitor/core';

import { appendAppLog } from './appLog';
import { formatiereZeitpunkt, geraeteName } from './appVersion';
import type { FileSubject, LogKind } from './storage';

/**
 * Zweitschriften in der Fotos-Mediathek.
 *
 * Schalter: **iPad-Einstellungen → „Dateien in Fotos speichern"**, Standard aus. Ist er an,
 * bekommt jede Bild- und Videoaufnahme zusätzlich eine Kopie in Fotos — beschriftet mit
 * Gerät, Zeitpunkt, Prüfung, Station, Aufgabe, Prüfling und Prüfer.
 *
 * ⚠️ **Die Kopie ist keine Zweitausfertigung des Belegs.** Der Beleg liegt unter Documents
 * und ist signiert; hier entsteht ein **neues** Bild mit hineingezeichnetem Text. Würde man
 * den Text ins Original zeichnen, wäre dessen Beglaubigung ungültig — und genau darauf beruht
 * die Beweiskraft.
 *
 * ⚠️ **Damit verlassen Prüfungsbilder den geschützten Bereich.** Die Mediathek ist auch im
 * geführten Zugriff über andere Apps erreichbar und wird je nach Einstellung nach iCloud
 * gesichert. Deshalb steht der Schalter bei der Einrichtung und nicht beim Prüfer, und
 * deshalb ist er aus, solange ihn niemand einschaltet.
 */

export interface PhotosPlugin {
  saveImage(options: {
    base64: string;
    album?: string;
    timestamp?: number;
  }): Promise<void>;
  /**
   * `lines` leer heisst: keine Beschriftung, kein zweiter Kodierlauf — das Video wandert
   * unverändert in die Mediathek.
   */
  saveVideo(options: {
    path: string;
    album?: string;
    timestamp?: number;
    lines?: string[];
  }): Promise<void>;
}

class PhotosWeb extends WebPlugin implements PhotosPlugin {
  async saveImage(): Promise<void> {
    throw new Error('Die Fotos-Mediathek gibt es nur auf dem Gerät.');
  }
  async saveVideo(): Promise<void> {
    throw new Error('Die Fotos-Mediathek gibt es nur auf dem Gerät.');
  }
}

export const Photos = registerPlugin<PhotosPlugin>('Photos', { web: () => new PhotosWeb() });

/* ------------------------------------------------------------------ Beschriftung */

/** Was auf der Kopie stehen soll. Alles, was fehlt, bleibt leer und entfällt in der Zeile. */
export interface Beschriftungsangaben {
  /** Der in den Einstellungen vergebene iPad-Name. */
  geraet: string;
  /** Zeitpunkt der Aufnahme. */
  zeitpunkt: Date;
  pruefung: string;
  station: string;
  aufgabe: string;
  pruefling: string;
  pruefer: string;
}

/** Wie lang ein Aufgabentext auf der Beschriftung werden darf, ehe er gekürzt wird. */
export const AUFGABE_MAX = 48;

/**
 * Kürzt am letzten Wort vor der Grenze, nicht mitten hinein.
 *
 * Ein an beliebiger Stelle abgeschnittener Aufgabentext liest sich wie ein anderer Text —
 * auf einem Beleg ist das schlimmer als eine Zeile, die sichtbar aufhört.
 */
export function kuerze(text: string, max = AUFGABE_MAX): string {
  const sauber = text.replace(/\s+/g, ' ').trim();
  if (sauber.length <= max) return sauber;
  const schnitt = sauber.slice(0, max);
  const luecke = schnitt.lastIndexOf(' ');
  return (luecke > max * 0.5 ? schnitt.slice(0, luecke) : schnitt).trimEnd() + '…';
}

/**
 * Die Zeilen der Beschriftung — drei, von grob nach fein.
 *
 * Leere Angaben fallen weg, statt als „—" Platz zu nehmen: auf einem Foto zählt jede Zeile,
 * und eine Station ohne Aufgabe ist ein normaler Fall (Kommentar auf Stationsebene).
 */
export function baueBeschriftung(a: Beschriftungsangaben): string[] {
  const verbinde = (...teile: string[]) => teile.filter((t) => t.trim() !== '').join(' · ');
  return [
    verbinde(a.geraet, formatiereZeitpunkt(a.zeitpunkt)),
    verbinde(a.pruefung, a.station, kuerze(a.aufgabe)),
    verbinde(
      a.pruefling ? 'Prüfling: ' + a.pruefling : '',
      a.pruefer ? 'Prüfer: ' + a.pruefer : '',
    ),
  ].filter((zeile) => zeile !== '');
}

/* --------------------------------------------------------------- Wer die Namen kennt */

/** Was die Oberfläche beisteuern muss: aus Kennungen werden Namen. */
export type BeschriftungsQuelle = (
  subject: FileSubject,
) => Pick<Beschriftungsangaben, 'pruefung' | 'station' | 'aufgabe' | 'pruefling' | 'pruefer'>;

/**
 * ⚠️ `platform/` darf nichts aus `store/` holen — sonst hinge die Ablage an der Oberfläche
 * und liesse sich nicht mehr für sich prüfen (dieselbe Regel wie beim Gerätenamen in
 * `appVersion.ts`). Die Oberfläche meldet deshalb an, wie aus Kennungen Namen werden.
 */
let quelle: BeschriftungsQuelle | null = null;
export const setBeschriftungsQuelle = (fn: BeschriftungsQuelle | null) => {
  quelle = fn;
};

const LEER = { pruefung: '', station: '', aufgabe: '', pruefling: '', pruefer: '' };

/** Die zwei Schalter der Einrichtung, wie sie nach der Rangfolge gelten. */
export interface Schalterstand {
  /** Entsteht überhaupt eine Zweitschrift? (`save_photos_in_album`) */
  kopieren: boolean;
  /** Wird Text hineingezeichnet? (`media_label`) */
  beschriften: boolean;
}

/**
 * Woher der Schalterstand kommt — angemeldet aus `main.tsx`, genau wie die Beschriftungsquelle
 * darüber.
 *
 * ⚠️ **Der Umweg ist der Punkt.** Früher las diese Datei die zwei Schalter unmittelbar aus den
 * iPad-Einstellungen (`Signing.getManagedSettings`). Damit galt hier als einziger Stelle der
 * App **nicht** die Rangfolge aus `einstellungen.ts`: ein gescannter QR-Code setzte jede
 * Einstellung ausser diesen beiden. Vorgabe des Nutzers vom 2026-09-15: der Code schlägt die
 * Einstellungen. Überschreibt eine Geräteverwaltung ihn danach wieder, ist das hingenommen —
 * das ist dieselbe Ordnung wie überall sonst (QR → MDM → iPad → App → Vorgabe).
 *
 * Gemischt wird nicht hier, sondern im Store; `platform/` darf nichts aus `store/` holen.
 */
export type SchalterQuelle = () => Schalterstand;

let schalterQuelle: SchalterQuelle | null = null;
export const setSchalterQuelle = (fn: SchalterQuelle | null) => {
  schalterQuelle = fn;
};

/* --------------------------------------------------------------------- Das Zeichnen */

/** Höhe des Textstreifens, gerechnet aus der Bildbreite — damit er auf jedem Format trägt. */
const streifenMasse = (breite: number, zeilen: number) => {
  const schrift = Math.max(13, Math.round(breite / 52));
  const zeilenhoehe = Math.round(schrift * 1.42);
  return { schrift, zeilenhoehe, hoehe: zeilenhoehe * zeilen + Math.round(schrift * 1.6) };
};

/**
 * Zeichnet das Bild neu und setzt den Textstreifen **unter** das Bild.
 *
 * Unter das Bild und nicht darüber: ein halbdurchsichtiger Balken auf dem Bild verdeckt
 * genau das, was der Prüfer festhalten wollte. Das Bild wird dadurch höher, nicht ärmer.
 */
export async function zeichneMitBeschriftung(
  datenUrl: string,
  zeilen: string[],
): Promise<string> {
  const bild = await new Promise<HTMLImageElement>((fertig, fehlschlag) => {
    const i = new Image();
    i.onload = () => fertig(i);
    i.onerror = () => fehlschlag(new Error('Bild liess sich nicht lesen.'));
    i.src = datenUrl;
  });

  // ⚠️ Ein SVG ohne `width`/`height` kommt mit der Grösse 0 an — die Kopie wäre ein leerer
  // Streifen mit Text. Lieber gar keine als eine, die aussieht, als sei nichts aufgenommen
  // worden.
  if (bild.width === 0 || bild.height === 0) {
    throw new Error('Das Bild hat keine Grösse (fehlt dem SVG width/height?).');
  }

  // Ohne Zeilen kein Streifen: die Kopie ist dann nur ein zweites, unbeschriftetes Bild.
  // Das ist der Normalfall, wenn der Schalter aus ist — und beim SVG der Handschrift der
  // einzige Weg, weil die Mediathek kein SVG nimmt und es gezeichnet werden muss.
  const { schrift, zeilenhoehe, hoehe } =
    zeilen.length === 0
      ? { schrift: 0, zeilenhoehe: 0, hoehe: 0 }
      : streifenMasse(bild.width, zeilen.length);
  const leinwand = document.createElement('canvas');
  leinwand.width = bild.width;
  leinwand.height = bild.height + hoehe;

  const c = leinwand.getContext('2d');
  if (!c) throw new Error('Keine Zeichenfläche.');

  c.drawImage(bild, 0, 0);
  c.fillStyle = '#111824';
  c.fillRect(0, bild.height, leinwand.width, hoehe);

  c.fillStyle = '#e8eefa';
  c.textBaseline = 'top';
  const rand = Math.round(schrift * 0.8);
  zeilen.forEach((zeile, i) => {
    // Die erste Zeile trägt Gerät und Zeitpunkt und steht kräftiger.
    c.font = (i === 0 ? '600 ' : '400 ') + schrift + 'px -apple-system, system-ui, sans-serif';
    c.fillText(zeile, rand, bild.height + rand + i * zeilenhoehe);
  });

  // Immer PNG: die Kopie wird gezeichnet, nicht kopiert, und ein zweites Mal JPEG über ein
  // bereits verlustbehaftetes Bild zu legen, kostet Zeichenschärfe ohne Gegenwert.
  return leinwand.toDataURL('image/png').split(',')[1];
}

/* ------------------------------------------------------------------------ Der Weg */

/** Welche Arten überhaupt in Fotos gehören. Ton nicht — die Mediathek kennt ihn nicht. */
const BILDARTEN: LogKind[] = ['screenshot', 'photo', 'handwriting'];

const mimeVon = (endung: string) => (endung === 'png' ? 'image/png' : 'image/jpeg');

/** Der Albumname: eine Prüfung, ein Album. Ohne das lägen die Kopien im Gesamtstrom. */
const albumVon = (pruefung: string) => 'fairexa Stage' + (pruefung ? ' · ' + pruefung : '');

/**
 * Die beiden Schalter der Einrichtung, in einem Zug gelesen.
 *
 * `kopieren` entscheidet, **ob** eine Zweitschrift entsteht; `beschriften`, ob Text
 * hineingezeichnet wird. Getrennt, weil sie Verschiedenes wollen: der erste, ob Prüfungsbilder
 * den geschützten Ordner verlassen, der zweite, ob die Kopie ohne Datenbank lesbar sein soll.
 *
 * ⚠️ **Ohne angemeldete Quelle wird nicht kopiert.** Das ist der Browser, der Test und die
 * Zeitspanne vor `main.tsx` — in allen dreien ist „lieber keine Kopie" die richtige von zwei
 * möglichen Auslegungen: es geht darum, ob Prüfungsbilder den geschützten Ordner verlassen.
 */
export function schalter(): Schalterstand {
  return schalterQuelle ? schalterQuelle() : { kopieren: false, beschriften: false };
}

/**
 * Wer Text bekommt und wer nicht.
 *
 * **Abzüge immer** (Entscheidung des Nutzers, 2026-09-13): Der Prüfer nimmt sie nicht auf — er
 * tippt auf „Bewerten", und die App hält den Bogen fest. Was er nicht aufnimmt, kann er auch
 * nicht bewusst ohne Beschriftung wollen.
 *
 * Alles Übrige hängt am Schalter „Aufgenommene Medien mit Text versehen". Die **Handschrift**
 * zählt dabei zu den Aufnahmen: der Prüfer erzeugt sie mit der Hand, sie ist seine Eingabe.
 */
export function sollBeschriften(kind: LogKind, schalterAn: boolean): boolean {
  return kind === 'screenshot' || schalterAn;
}

function angaben(subject: FileSubject, zeitpunkt: Date): Beschriftungsangaben {
  const namen = quelle ? quelle(subject) : LEER;
  return { geraet: geraeteName(), zeitpunkt, ...namen };
}

/**
 * Legt eine Bildkopie in Fotos — wenn der Schalter an ist.
 *
 * ⚠️ **Wirft nie.** Die Kopie ist eine Bequemlichkeit; der Beleg liegt längst geschrieben und
 * beglaubigt unter Documents. Ein Fehlschlag darf weder das Speichern noch den Prüfer
 * aufhalten und geht ins Dateilog.
 */
export async function kopiereBildNachFotos(
  kind: LogKind,
  subject: FileSubject,
  extension: string,
  base64: string,
  zeitpunkt = new Date(),
): Promise<void> {
  if (!BILDARTEN.includes(kind)) return;
  try {
    const s = schalter();
    if (!s.kopieren) return;
    const a = angaben(subject, zeitpunkt);
    // Ohne Beschriftung geht das Bild, wie es ist, in die Mediathek — kein zweites Zeichnen,
    // kein zweites Kodieren, keine Schärfe verloren.
    const bild = sollBeschriften(kind, s.beschriften)
      ? await zeichneMitBeschriftung(
          'data:' + mimeVon(extension) + ';base64,' + base64,
          baueBeschriftung(a),
        )
      : base64;
    await Photos.saveImage({
      base64: bild,
      album: albumVon(a.pruefung),
      timestamp: zeitpunkt.getTime(),
    });
  } catch (fehler) {
    await appendAppLog('Kopie nach Fotos fehlgeschlagen: ' + String(fehler), 'warn');
  }
}

/**
 * Dasselbe für die Handschrift, die als **SVG-Text** vorliegt.
 *
 * Eigener Weg statt Base64: die Zeichenkette lässt sich unmittelbar als Daten-URL geben, und
 * `btoa` kommt mit Umlauten in einer Aufgabenbeschriftung nicht zurecht.
 */
export async function kopiereSvgNachFotos(
  kind: LogKind,
  subject: FileSubject,
  svg: string,
  zeitpunkt = new Date(),
): Promise<void> {
  if (!BILDARTEN.includes(kind)) return;
  try {
    const s = schalter();
    if (!s.kopieren) return;
    const a = angaben(subject, zeitpunkt);
    // ⚠️ Gezeichnet wird hier **immer**: die Mediathek nimmt kein SVG. Ist der Schalter aus,
    // bleibt der Streifen weg, das Bild entsteht trotzdem.
    const mitText = await zeichneMitBeschriftung(
      'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg),
      sollBeschriften(kind, s.beschriften) ? baueBeschriftung(a) : [],
    );
    await Photos.saveImage({
      base64: mitText,
      album: albumVon(a.pruefung),
      timestamp: zeitpunkt.getTime(),
    });
  } catch (fehler) {
    await appendAppLog('Kopie der Handschrift nach Fotos fehlgeschlagen: ' + String(fehler), 'warn');
  }
}

/**
 * Legt eine Videokopie in Fotos — wenn der Schalter an ist.
 *
 * ⚠️ **Die Beschriftung kostet hier einen zweiten vollständigen Kodierlauf.** Der erste
 * geschieht schon beim Zusammenfügen der Abschnitte (`VideoSegmentMerger.swift`); der zweite
 * zeichnet den Textstreifen unter das Bild (`VideoLabelRenderer.swift`) und dauert auf einem
 * älteren iPad bei einer längeren Aufnahme Minuten.
 *
 * Deshalb läuft er **im Hintergrund und ohne Anzeige**, unmittelbar nach dem Ablegen
 * (Entscheidung des Nutzers, 2026-09-13): Der Beleg liegt geschrieben und beglaubigt unter
 * Documents — die Kopie darf sich Zeit lassen. Ist der Schalter aus, bleibt es beim schnellen
 * Weg ohne Text; die Zuordnung trägt dann der Albumname und das gesetzte Aufnahmedatum.
 */
export async function kopiereVideoNachFotos(
  pfad: string,
  subject: FileSubject,
  zeitpunkt = new Date(),
): Promise<void> {
  try {
    const s = schalter();
    if (!s.kopieren) return;
    const a = angaben(subject, zeitpunkt);
    const zeilen = sollBeschriften('video', s.beschriften) ? baueBeschriftung(a) : [];
    if (zeilen.length > 0) {
      await appendAppLog('Videokopie wird beschriftet (zweiter Kodierlauf): ' + pfad);
    }
    await Photos.saveVideo({
      path: pfad,
      album: albumVon(a.pruefung),
      timestamp: zeitpunkt.getTime(),
      lines: zeilen,
    });
  } catch (fehler) {
    await appendAppLog('Videokopie nach Fotos fehlgeschlagen: ' + String(fehler), 'warn');
  }
}
